<script setup lang="ts">
import { useTranslate } from "@/composables/useTranslate";
import { InstallerDetails, Worker } from "@/types";
import { defineProps, defineEmits, watch, nextTick, ref } from "vue";
import { useRoute } from "vue-router";

// Route
const route = useRoute();
const { t } = useTranslate();

// Props
const props = defineProps<Props>();
type Props = {
  workers?: Worker[];
  installerDetails?: InstallerDetails;
};

const selectedWorker = ref<Worker | null>(null);

const isInstallerReportMenuOpen = ref<boolean>(true);

watch(
  () => route.params.reportId,
  async (newReportId) => {
    if (newReportId) {
      await nextTick();

      setTimeout(() => {
        const el = document.getElementById(newReportId as string);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  },
  { immediate: true }
);

watch(
  () => props.installerDetails?.bonusReports,
  async () => {
    await nextTick();
    setTimeout(() => {
      const el = document.getElementById(route.params.reportId as string);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  }
);

watch(
  () => route.params.id,
  async (newWorkerId) => {
    if (newWorkerId) {
      await nextTick();
      setTimeout(() => {
        const el = document.getElementById(newWorkerId as string);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  },
  { immediate: true }
);

// Automatically find the selected worker based on the route
watch(
  () => route.params.id,
  (newId: string | string[]) => {
    if (newId && props.workers?.length) {
      const foundWorker = props.workers?.find(
        (worker: Worker) => String(worker.id) === String(newId)
      );
      selectedWorker.value = foundWorker || null;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="max-w-[190px] w-full h-auto overflow-hidden">
    <div
      class="w-full rounded-[4px] m-[2px] mt-[1px] ml-[1px] py-4 px-3 bg-[#E5E4E2]"
    >
      <h4
        class="text-base leading-[26px] tracking-[0.01rem] uppercase font-medium text-[#51687A]"
      >
        {{ t("installers.reports") }}
      </h4>
      <div class="w-full flex flex-col items-start mt-2">
        <!-- Review -->
        <RouterLink
          to="/installers/monthly-bonus"
          class="w-fit flex items-center gap-1 cursor-pointer p-[6px] rounded-primary-radius hover:bg-[#D3D1CD] duration-300"
          :class="`${route.path.includes('monthly-bonus') ? 'bg-white' : ''} `"
        >
          <img src="@/assets/gift.svg" alt="Gift" />
          <h5
            class="text-[#393840] font-medium text-sm tracking-[0.01rem] pr-1"
          >
            {{ t("installers.monthly_bonus") }}
          </h5>
        </RouterLink>
      </div>
    </div>
    <div
      class="w-full h-[calc(100vh-155px)] overflow-y-auto rounded-[4px] m-[2px] mt-[1px] ml-[1px] pt-4 pb-2 px-3 bg-[#E5E4E2]"
    >
      <div>
        <h4
          class="text-base leading-[26px] tracking-[0.01rem] uppercase font-medium text-[#51687A]"
        >
          {{ t("installers.installers") }}
        </h4>
      </div>
      <RouterLink
        to="/installers"
        class="w-fit flex items-center gap-1 mt-2 mb-[2px] p-[6px] rounded-primary-radius cursor-pointer hover:bg-[#D3D1CD] duration-300"
        :class="route.path === '/installers' ? 'bg-white' : 'bg-transparent'"
      >
        <img class="w-[15px] h-[15px]" src="@/assets/users-v2.svg" alt="" />
        <span class="text-[#393840] font-medium text-sm tracking-[0.01rem]">{{
          t("installers.all_installers")
        }}</span>
      </RouterLink>

      <div class="w-full flex flex-col items-start">
        <div
          v-if="selectedWorker && selectedWorker.id === route.params.id"
          @click="
            () => {
              if (!route.path.includes('report')) {
                isInstallerReportMenuOpen = !isInstallerReportMenuOpen;
              }
            }
          "
        >
          <RouterLink
            :to="`/installers/${selectedWorker.id}`"
            class="w-full flex items-center gap-1 cursor-pointer p-[6px] rounded-primary-radius hover:bg-[#D3D1CD] duration-300"
          >
            <img
              v-show="route.params.id !== selectedWorker.id"
              src="@/assets/user-v2.svg"
              alt="Users"
            />
            <img
              v-if="
                route.params.id === selectedWorker.id &&
                !isInstallerReportMenuOpen
              "
              src="@/assets/caret_down.svg"
              alt="Caret down"
            />
            <img
              class="-rotate-180"
              v-show="
                route.params.id === selectedWorker.id &&
                isInstallerReportMenuOpen
              "
              src="@/assets/caret_down.svg"
              alt="caret up"
            />
            <h5 class="text-[#393840] font-medium text-sm tracking-[0.01rem]">
              {{ selectedWorker.fullname }}
            </h5>
          </RouterLink>

          <ul
            v-if="
              route.params.id === selectedWorker.id && isInstallerReportMenuOpen
            "
            class="mt-[3px] pl-4"
          >
            <li
              class="w-full flex flex-col gap-[2px] p-[2px] pl-[6px] border-l-[2px] border-[#D3D1CD]"
            >
              <div
                class="w-full relative text-sm p-[6px] rounded-primary-radius bg-transparent text-primary-text-color font-medium tracking-[0.01rem] cursor-pointer hover:bg-[#D3D1CD] duration-300"
                :class="[
                  !route.params.reportId
                    ? 'bg-white before:absolute before:top-0 before:-left-2 before:h-full before:w-[2px] before:bg-[#51687A]'
                    : 'bg-transparent',
                ]"
                :id="selectedWorker.id"
              >
                <RouterLink :to="`/installers/${selectedWorker.id}`">
                  {{ t("installers.current_month") }}
                </RouterLink>
              </div>

              <div v-if="installerDetails && installerDetails.bonusReports">
                <div
                  v-for="report in installerDetails.bonusReports"
                  :key="report.id"
                  class="w-full relative text-sm p-[6px] rounded-primary-radius bg-transparent text-primary-text-color font-medium tracking-[0.01rem] cursor-pointer hover:bg-[#D3D1CD] duration-300"
                  :class="[
                    report.id === Number(route.params.reportId)
                      ? 'bg-white before:absolute before:top-0 before:-left-2 before:h-full before:w-[2px] before:bg-[#51687A]'
                      : 'bg-transparent',
                  ]"
                  :id="String(report.id)"
                >
                  <RouterLink
                    :to="`/installers/${selectedWorker.id}/report/${report.id}`"
                    class="w-full flex items-start"
                  >
                    {{ report.month + " " + report.year }}
                  </RouterLink>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
